import { type ChangeEvent, useCallback } from "react";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ScreenshotMonitorOutlinedIcon from "@mui/icons-material/ScreenshotMonitorOutlined";
import Button from "@mui/material/Button";
import { notify } from "src/utils/notification";
import {
  ATTACHMENT_TOO_LARGE_MESSAGE,
  CLIPBOARD_EMPTY_MESSAGE,
} from "../../FeedbackDialog.constants";
import {
  type ImageReadResult,
  readClipboardImage,
  readImageAsDataUrl,
  usePasteImage,
  useScreenshotCapture,
} from "../../FeedbackDialog.hooks";
import type { Attachment, AttachmentKind } from "../../FeedbackDialog.types";
import FeedbackDialogAttachmentPreview from "./components/FeedbackDialogAttachmentPreview/FeedbackDialogAttachmentPreview";

type FeedbackDialogAttachmentsProps = {
  attachment: Attachment | null;
  onAttachmentChange: (attachment: Attachment | null) => void;
};

const FeedbackDialogAttachments = ({
  attachment,
  onAttachmentChange,
}: FeedbackDialogAttachmentsProps) => {
  const { capture, capturing } = useScreenshotCapture();

  // Apply a read result: attach on success, warn on oversize, stay silent for
  // non-image / read errors (consistent with the prior silent-drop behavior).
  const applyImageResult = useCallback(
    (result: ImageReadResult, kind: AttachmentKind, name?: string) => {
      if (result.ok) {
        onAttachmentChange({ kind, src: result.src, name });
      } else if (result.reason === "too-large") {
        notify(ATTACHMENT_TOO_LARGE_MESSAGE, { variant: "warning" });
      }
    },
    [onAttachmentChange]
  );

  const handlePastedImage = useCallback(
    (result: ImageReadResult, name?: string) =>
      applyImageResult(result, "paste", name),
    [applyImageResult]
  );

  usePasteImage(handlePastedImage);

  const handleCapture = async () => {
    const src = await capture();
    if (src) {
      onAttachmentChange({ kind: "capture", src });
    }
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      applyImageResult(await readImageAsDataUrl(file), "upload", file.name);
    }
    event.target.value = "";
  };

  const handlePasteClick = async () => {
    const result = await readClipboardImage();
    if (result) {
      applyImageResult(result, "paste");
    } else {
      notify(CLIPBOARD_EMPTY_MESSAGE, { variant: "warning" });
    }
  };

  return (
    <div>
      <span className="mb-2 block text-sm text-text-secondary">
        Attachments (optional)
      </span>
      <div className="flex flex-wrap gap-3">
        <Button
          type="button"
          variant="outlined"
          disabled={capturing}
          aria-busy={capturing}
          onClick={handleCapture}
          startIcon={<ScreenshotMonitorOutlinedIcon />}
          className="w-fit"
        >
          Capture Screenshot
        </Button>
        <Button
          component="label"
          variant="outlined"
          startIcon={<ImageOutlinedIcon />}
          className="w-fit"
        >
          Upload Image
          <input type="file" accept="image/*" hidden onChange={handleFileChange} />
        </Button>
        <Button
          type="button"
          variant="outlined"
          onClick={handlePasteClick}
          startIcon={<ContentPasteOutlinedIcon />}
          className="w-fit"
        >
          Paste
        </Button>
      </div>
      <span className="mt-2 block text-xs text-text-secondary">
        Tip: paste a screenshot directly with Ctrl/Cmd+V.
      </span>
      {attachment && (
        <div className="mt-3">
          <FeedbackDialogAttachmentPreview
            attachment={attachment}
            onRemove={() => onAttachmentChange(null)}
          />
        </div>
      )}
    </div>
  );
};

export default FeedbackDialogAttachments;
